/**
 * 자체 에러 모니터링 시스템 - 에러 리포터.
 *
 * 에러를 백엔드 API(POST /api/v1/error-logs)로 전송합니다.
 * axios 대신 fetch를 사용하여 순환 의존을 방지합니다.
 */

// `@/` 별칭 대신 상대경로 — npm test(tsx --test)가 tsconfig paths 를 읽지 못해
// 별칭 import 가 있으면 단위 테스트에서 모듈 해석이 실패한다.
import { STORAGE_KEYS } from './constants'
import { safeLocalStorage } from './safeStorage'

interface ErrorContext {
  url?: string
  componentStack?: string
  extra?: Record<string, unknown>
}

/**
 * 외부(cross-origin) 스크립트 에러는 브라우저 보안 정책상 메시지가
 * "Script error." 로만 노출되고 source/lineno/colno 모두 빈 값이라
 * 디버깅 가치가 없다. 보고에서 제외해 노이즈를 차단한다.
 * (AdSense·네이버맵·인앱브라우저 등에서 가장 빈번)
 */
function isCrossOriginScriptError(error: unknown, context?: ErrorContext): boolean {
  const message = error instanceof Error ? error.message : String(error)
  if (!/^Script error\.?$/i.test(message.trim())) return false
  const extra = context?.extra as { source?: unknown; lineno?: unknown; colno?: unknown } | undefined
  const noSource = !extra?.source
  const zeroLine = !extra?.lineno || extra.lineno === 0
  const zeroCol = !extra?.colno || extra.colno === 0
  return noSource && zeroLine && zeroCol
}

/**
 * 브라우저 자동완성/확장이 cross-origin iframe(시설 상세의 네이버 지도 등)에
 * 접근하려다 브라우저 보안정책(same-origin)에 막혀 던지는 SecurityError.
 * 우리 코드가 아니라 브라우저가 주입한 스크립트(예: 네이버 웨일 autofill
 * `extractFormsAndFormElements_`)에서 발생하며 지도·페이지 기능에는 영향이 없다.
 * 보고에서 제외해 노이즈를 차단한다.
 * (2026-07-18: 시설 상세 /facilities/* 에서 "Blocked a frame..." 반복 기록 원인)
 */
function isCrossOriginFrameError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /Blocked a frame with origin|cross-origin frame/i.test(message)
}

/**
 * 우리 코드가 아닌 외부 스크립트에서 발생한 에러.
 *
 * 스택에 URL 이 하나라도 있는데 그중 우리 오리진이 단 하나도 없으면
 * 우리 번들은 호출 경로에 없었다는 뜻이다 — 브라우저 확장(메타마스크·위치
 * 스푸핑 등 `chrome-extension://`), 애드센스, 인앱브라우저 주입 스크립트가
 * 모두 여기에 해당한다. 우리가 고칠 수 없는 남의 코드라 보고에서 제외한다.
 *
 * 호스트 목록을 두지 않고 오리진 유무만 보는 이유는, 확장 ID 나 광고 도메인이
 * 늘어날 때마다 필터를 따라 고칠 필요가 없기 때문이다. 반대로 우리 번들
 * 프레임이 한 줄이라도 섞여 있으면 우리 책임일 수 있으므로 그대로 보고한다.
 * (2026-09-06: `Failed to connect to MetaMask` 등 30일치 프론트 에러의
 *  대부분이 확장·애드센스 발 노이즈였다)
 */
function isForeignScriptError(error: unknown, context?: ErrorContext): boolean {
  if (typeof window === 'undefined') return false
  const origin = window.location.origin

  // window.onerror 는 error 객체 없이 source(스크립트 URL)만 오는 경우가 있다.
  const source = (context?.extra as { source?: unknown } | undefined)?.source
  if (typeof source === 'string' && /^[\w-]+-extension:\/\//.test(source)) return true

  const stack = error instanceof Error ? error.stack : undefined
  if (!stack) return false
  const urls = stack.match(/(?:https?|[\w-]+-extension):\/\/[^\s)]+/g)
  if (!urls) return false
  return !urls.some((u) => u.startsWith(origin))
}

/**
 * CefSharp 계열 임베디드 브라우저(링크 검사 봇 등)의 JS↔호스트 브리지 실패 문자열.
 *
 * `Object Not Found Matching Id:5, MethodName:update, ParamCount:4` 형태를 문자열 그대로
 * reject 해 unhandledrejection 으로 올라온다. Error 객체가 아니라 stack 이 없어서
 * `isForeignScriptError` 의 `if (!stack) return false` 를 그냥 통과한다 — 그래서 메시지
 * 패턴으로 따로 막는다.
 *
 * 부분 일치가 아니라 전체 일치로 판정한다. 같은 문구를 품은 우리 코드 에러까지 삼키면
 * 진짜 장애를 놓치기 때문이다.
 * (2026-09-20: Azure 대역 봇 하나가 프론트 에러 36건·어드민 알림 108건을 만든 원인)
 */
function isCefSharpBridgeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/.test(message)
}

/**
 * 에러를 백엔드로 전송합니다.
 * 전송 실패 시 콘솔 경고만 출력하고 앱을 중단시키지 않습니다.
 */
export async function reportError(error: unknown, context?: ErrorContext): Promise<void> {
  if (
    isCrossOriginScriptError(error, context) ||
    isCrossOriginFrameError(error) ||
    isForeignScriptError(error, context) ||
    isCefSharpBridgeError(error)
  ) {
    return
  }

  try {
    // Error가 아닌 객체(reject({...}) 등)는 String()이 "[object Object]"가 되어 정보가 사라진다
    // — JSON 직렬화를 우선 시도해 디버깅 단서를 남긴다.
    const message = error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null
        ? (() => { try { return JSON.stringify(error) } catch { return String(error) } })()
        : String(error)
    const stackTrace = error instanceof Error ? error.stack : undefined

    // 로그인 사용자 정보 추출
    let userId: number | null = null
    try {
      const userStr = safeLocalStorage.getItem(STORAGE_KEYS.USER)
      if (userStr) {
        const user = JSON.parse(userStr)
        userId = user.id ?? null
      }
    } catch {
      // JSON.parse 실패 무시
    }

    const extraData = context?.extra || context?.componentStack
      ? JSON.stringify({
          ...(context?.componentStack ? { componentStack: context.componentStack } : {}),
          ...(context?.extra ?? {}),
        })
      : undefined

    const body = {
      source: 'FRONTEND',
      level: 'ERROR',
      message: message.substring(0, 2000), // 메시지 길이 제한
      stackTrace: stackTrace?.substring(0, 10000),
      url: context?.url ?? (typeof window !== 'undefined' ? window.location.pathname : '/'),
      userId,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'SSR',
      extraData,
    }

    await fetch('/api/v1/error-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (e) {
    console.warn('[ErrorReporter] 에러 전송 실패:', e)
  }
}

/**
 * 글로벌 에러 핸들러를 등록합니다.
 * main.tsx에서 앱 초기화 시 호출해야 합니다.
 */
export function initGlobalErrorHandlers(): void {
  // 잡히지 않은 예외
  window.onerror = (message, source, lineno, colno, error) => {
    reportError(error ?? message, {
      url: window.location.pathname,
      extra: {
        source: source ?? undefined,
        lineno: lineno ?? undefined,
        colno: colno ?? undefined,
      },
    })
  }

  // 잡히지 않은 Promise rejection
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    // 이유(reason) 없이 reject된 프로미스는 디버깅 단서가 전혀 없다.
    // 인앱 브라우저(네이버 등)가 페이지에 주입하는 외부 스크립트에서 상시 발생하는
    // 노이즈라 보고에서 제외한다 — 위 "Script error." 필터와 같은 정책.
    // (2026-07-05: 시설 상세에서 message "undefined" 반복 기록 원인)
    if (event.reason === undefined || event.reason === null || event.reason === '') return
    reportError(event.reason, {
      url: window.location.pathname,
      extra: { type: 'unhandledrejection' },
    })
  }

  console.info('[ErrorReporter] 글로벌 에러 핸들러가 등록되었습니다.')
}
